<template>
    <div
        v-if="errorMessages?.length > 0 || warningMessages?.length > 0"
        class="d-flex flex-column gap-3 eq-messagecontainer"
        :class="{ 'overflow-auto': messageLength > 10 }"
    >
        <AlertMessage
            type="danger"
            :show-icon="false"
            class="flex-fill"
            innerclass="flex-fill"
            @close="onCloseMessages('danger')"
            v-if="errorMessages?.length > 0"
        >
            <AlertMessage
                type="danger"
                class="mb-1"
                size="xs"
                v-for="message in errorMessages"
                :key="message.dismisskey"
                :show-icon="false"
                :dismisskey="message.dismisskey"
                :fulldismiss-button="true"
                :globalmessage="true"
            >
                <div class="d-flex">
                    <span v-html="message.message"></span>
                    <RouterLink
                        class="ms-3"
                        v-if="message.routeName && message.routeParams"
                        :route="message.routeName"
                        v-bind="message.routeParams"
                        >{{ $tm('globalmessage.resolveissue') }}</RouterLink
                    >
                </div>
            </AlertMessage>
        </AlertMessage>

        <AlertMessage
            :show-icon="false"
            class="flex-fill"
            innerclass="flex-fill"
            @close="onCloseMessages('warning')"
            v-if="warningMessages?.length > 0"
        >
            <AlertMessage
                type="warning"
                class="mb-1"
                size="xs"
                v-for="message in warningMessages"
                :key="message.dismisskey"
                :show-icon="false"
                :dismisskey="message.dismisskey"
                :fulldismiss-button="true"
                :globalmessage="true"
                ><div class="d-flex">
                    <span v-html="message.message"></span>
                    <RouterLink
                        class="ms-3"
                        v-if="message.routeName && message.routeParams"
                        :route="message.routeName"
                        v-bind="message.routeParams"
                        >{{ $tm('globalmessage.resolveissue') }}</RouterLink
                    >
                </div>
            </AlertMessage>
        </AlertMessage>
    </div>
</template>

<script lang="ts" setup>
import { AlertMessage, IconComp, RouterLink } from '@/components';
import { useDismissedMessagesRef, useGlobalMessages } from '@/composeables';
import { useDeviceStatus } from '@/composeables/devicestatus/devicestatus';
import { $tm } from '@/locize';
import type { AlertMessageSrc, AlertMessageType, IAlertMessage } from '@/objects';
import { storeToRefs } from 'pinia';
import { computed, toRaw } from 'vue';

const { Messages } = useGlobalMessages();

const { DismissedMessages } = useDismissedMessagesRef();

const props = defineProps<{
    /**
     * If we should show the messages or not
     * @default true
     */
    showAllMessages: boolean;
}>();

// We need to know if we want to show the messages or not from DeviceStatus source
const { ListenForGlobalStatuses } = storeToRefs(useDeviceStatus());

// Get the warning messages
const warningMessages = computed(() => {
    return getSourceAlertsByType('warning', 'DeviceStatus')?.filter(
        (x) => x.dismisskey === undefined || DismissedMessages.value?.includes(x.dismisskey) === false,
    );
});

// Get the error messages
const errorMessages = computed(() => {
    return getSourceAlertsByType('danger', 'DeviceStatus')?.filter(
        (x) => x.dismisskey === undefined || DismissedMessages.value?.includes(x.dismisskey) === false,
    );
});

/**
 * Helper method to get the correct messages by type and source
 */
const getSourceAlertsByType = (type: AlertMessageType, src: AlertMessageSrc) => {
    let _messages: IAlertMessage[] = [];
    for (const value of Messages.values()) {
        value.forEach((x) => {
            if (
                (x.src == undefined || props.showAllMessages || (x.src == src && ListenForGlobalStatuses.value)) &&
                x.type == type &&
                x.hidden !== true
            ) {
                _messages.push(x);
            }
        });
    }

    return _messages;
};

const messageLength = computed(() => {
    return errorMessages.value?.length + warningMessages.value?.length;
});

/**
 * Close the warning messages
 */
const onCloseMessages = (type: AlertMessageType) => setMessageTypeHidden(type);

/**
 * Set the message type to hidden
 * @param messageType The message type to hide
 */
const setMessageTypeHidden = (messageType: AlertMessageType) => {
    let rawMessages = toRaw(Messages);

    for (const value of rawMessages.values()) {
        value.forEach((x) => {
            if (x.type == messageType) {
                x.hidden = true;
            }
        });
    }
};
</script>
